// src/pages/AccountSettings.tsx
// Updated: 2026-08-20 15:00:00 CDT
// Purpose: Let an authenticated TableRead user change their own password.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import PasswordFields from "@/components/PasswordFields";
import { useAuth } from "@/auth/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { validateNewPassword } from "@/utils/passwordSecurity";
import RouteHeader from '@/components/RouteHeader';
import { applySessionMode, sessionMode, type SessionMode } from '@/utils/sessionStorage';

const AccountSettings = () => {
  const { user } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loginMode,setLoginMode]=useState<SessionMode>(sessionMode);
  const [savingLogin,setSavingLogin]=useState(false);
  const [loginError,setLoginError]=useState('');
  const handleSessionPreference=async()=>{
    setSavingLogin(true);setLoginError('');
    try {
      await applySessionMode(loginMode,()=>supabase.auth.signOut({scope:'local'}),()=>window.location.assign('/login'));
    } catch {setLoginError('Unable to apply the login preference. Please retry.');}
    finally{setSavingLogin(false);}
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const validationError = validateNewPassword(password, confirmation);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        throw error;
      }

      setPassword("");
      setConfirmation("");
      setSuccessMessage("Your password has been updated.");
      console.debug("[AccountSettings] Password updated successfully.");
    } catch (error) {
      console.error("[AccountSettings] Password update failed:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to update your password. Use the password-reset email instead.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RouteHeader account/>

      <main className="mx-auto max-w-xl space-y-6 px-4 py-10">
        <Card>
          <CardHeader><CardTitle>Login persistence</CardTitle><CardDescription>Choose how this browser remembers your login. This does not change your other devices.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <fieldset disabled={savingLogin} className="space-y-3">
              <legend className="sr-only">Login persistence</legend>
              <label className="flex gap-3 rounded-lg border p-3"><input type="radio" name="login-persistence" value="persistent" checked={loginMode==='persistent'} onChange={()=>setLoginMode('persistent')}/><span><span className="block font-medium">Keep me signed in</span><span className="text-sm text-muted-foreground">For a private device. Retains login across browser restarts until you sign out or the session is revoked. Access tokens refresh automatically.</span></span></label>
              <label className="flex gap-3 rounded-lg border p-3"><input type="radio" name="login-persistence" value="tab" checked={loginMode==='tab'} onChange={()=>setLoginMode('tab')}/><span><span className="block font-medium">This tab only — public computer</span><span className="text-sm text-muted-foreground">No inactivity timer. Refreshing this tab keeps you signed in; closing it normally removes its stored login. New tabs do not use a remembered persistent login.</span></span></label>
            </fieldset>
            <p className="text-sm text-muted-foreground">Browsers may restore tab storage when restoring closed tabs or a previous session. Always use Sign Out before leaving a public computer.</p>
            <p className="text-sm text-muted-foreground">Applying a different choice signs you out here (including other persistent tabs in this browser). Sign in again to use the new choice. Your password is not stored.</p>
            {loginError&&<p role="alert" className="text-sm text-destructive">{loginError}</p>}
            <Button type="button" disabled={savingLogin||loginMode===sessionMode()} onClick={()=>void handleSessionPreference()}>{savingLogin?'Applying…':'Save login preference and sign out'}</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
            <CardDescription>
              Signed in as {user?.email}. Choose a unique password with at least eight characters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordFields
                password={password}
                confirmation={confirmation}
                disabled={submitting}
                onPasswordChange={setPassword}
                onConfirmationChange={setConfirmation}
              />

              {errorMessage && (
                <div className="space-y-2" role="alert">
                  <p className="text-sm text-destructive">{errorMessage}</p>
                  <Link className="text-sm text-primary underline" to="/forgot-password">
                    Send a password-reset email instead
                  </Link>
                </div>
              )}

              {successMessage && (
                <p className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm" role="status">
                  {successMessage}
                </p>
              )}

              <Button type="submit" disabled={submitting}>
                {submitting ? "Updating password…" : "Change password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AccountSettings;
